/**
 * As regras PURAS do seletor de cenários (lote de 09/10/2026, item 4) — sem DOM, sem
 * banco, testadas em `tests/cenarios-regras.test.ts`. A tela (`telas/cenario.ts`,
 * `ui/seletor-cenario.ts`) só desenha o que sai daqui.
 *
 * Nenhuma delas é PERMISSÃO: quem barra é o banco (0028). Elas existem para a tela dizer
 * o motivo ANTES do clique, em vez de oferecer um botão e responder "não pode" depois.
 */

/** O teto de cenários ATIVOS por empreendimento — o mesmo do gatilho `chk_limite_cenarios`. */
export const LIMITE_CENARIOS = 5;

interface CenarioMinimo {
  id: string;
  nome: string;
  criadoEm: string;
}

/**
 * Qual cenário abre ao entrar no empreendimento: o último que ESTA pessoa abriu nele,
 * neste navegador; se ele não estiver mais entre os ativos (arquivado, excluído, outro
 * navegador), o mais antigo. Decisão declarada no plano aprovado em 09/10/2026.
 *
 * "Mais antigo" pela data de criação e, no empate, pelo id — a ordem não pode depender
 * de como o banco devolveu a lista.
 */
export function escolherCenarioPadrao<T extends CenarioMinimo>(ativos: readonly T[], ultimoId: string | null): T | null {
  if (ativos.length === 0) return null;
  if (ultimoId !== null) {
    const lembrado = ativos.find((c) => c.id === ultimoId);
    if (lembrado !== undefined) return lembrado;
  }
  return [...ativos].sort((a, b) => (a.criadoEm < b.criadoEm ? -1 : a.criadoEm > b.criadoEm ? 1 : a.id < b.id ? -1 : 1))[0]!;
}

export interface Selo {
  texto: string;
  /** a classe do `.badge` */
  tom: 'out' | 'ok' | 'warn';
  dica?: string;
}

/**
 * O selo de cada cenário no seletor.
 *
 * O DEFEITO QUE ISTO CORRIGE: a lista antiga marcava "Publicado vN · com alterações" quando
 * `versao > versaoPublicada` — e num cenário publicado isso é SEMPRE verdade (salvar soma 1
 * em `versao`, publicar soma 1 em `versaoPublicada`; a diferença fica em 1 para sempre).
 * Todo cenário publicado aparecia "com alterações". Por isso a versão de edição nem entra
 * aqui: "alterações não publicadas" vem do RASCUNHO, que é por pessoa — só aparece para
 * quem tem trabalho guardado naquele cenário.
 */
export function seloCenario(versaoPublicada: number, temRascunhoMeu: boolean): Selo {
  const base = versaoPublicada > 0 ? `Publicado v${versaoPublicada}` : 'Nunca publicado';
  if (temRascunhoMeu) {
    return {
      texto: `${base} · alterações não publicadas`,
      tom: 'warn',
      dica: 'Você tem alterações neste cenário que ainda não publicou. Elas ficam guardadas só para você.',
    };
  }
  return { texto: base, tom: versaoPublicada > 0 ? 'ok' : 'out' };
}

/**
 * Mesma régua do índice `cenario_nome_ativo_uk`: `lower(btrim(nome))` — o `btrim` de 1 argumento
 * tira das pontas SÓ o espaço comum (U+0020); tab, quebra de linha e U+00A0 contam como conteúdo.
 * O que se GRAVA continua aparado pelo `trim` do JS (`perguntar()` e `exigirTexto()`), então nome
 * digitado nunca leva espaço nenhum nas pontas; esta régua só decide CONFLITO, e precisa bater com
 * o banco também para nome gravado por fora do app (revisão de 11/10/2026, ORG-7).
 * Resíduo conhecido: `toLowerCase` (JS) e `lower()` (lc_ctype do banco) podem divergir em letras
 * exóticas (İ, ß); o banco é a régua final, e o 23505 já vira a mensagem NOME_DUPLICADO.
 */
export function chaveDeNome(nome: string): string {
  return nome.replace(/^ +| +$/g, '').toLowerCase();
}

/**
 * Outro cenário ATIVO já usa este nome? (criar, copiar, renomear, restaurar). `excetoId`
 * é o próprio cenário no caso de renomear — trocar só maiúsculas não é conflito.
 */
export function nomeEmUso(ativos: ReadonlyArray<{ id: string; nome: string }>, nome: string, excetoId: string | null = null): boolean {
  const k = chaveDeNome(nome);
  return ativos.some((c) => c.id !== excetoId && chaveDeNome(c.nome) === k);
}

/** `null` = há vaga. Senão, a frase que explica por que "Novo"/"Copiar"/"Restaurar" não vão. */
export function motivoSemVaga(qtdAtivos: number): string | null {
  return qtdAtivos >= LIMITE_CENARIOS
    ? `Este empreendimento já tem ${LIMITE_CENARIOS} cenários ativos. Arquive um para liberar a vaga.`
    : null;
}

/**
 * Por que este ARQUIVADO não pode ser restaurado agora (`null` = pode).
 *
 * A lista de ativos em mãos pode estar VELHA e ainda trazer o próprio arquivado — um colega o
 * arquivou com a minha tela aberta, e a faixa da sonda manda restaurá-lo em "Arquivados". Ele
 * não ocupa vaga nem "usa" o próprio nome: contado, a regra recusava SEMPRE, dizendo que já
 * existia um cenário ativo com aquele nome (era ele mesmo) ou que não havia vaga. Por isso o
 * próprio sai da conta. O banco (0028) continua sendo a régua final.
 */
export function motivoParaNaoRestaurar(
  ativos: ReadonlyArray<{ id: string; nome: string }>,
  arquivado: { id: string; nome: string },
): string | null {
  const outros = ativos.filter((c) => c.id !== arquivado.id);
  const semVaga = motivoSemVaga(outros.length);
  if (semVaga !== null) return `Não foi possível restaurar "${arquivado.nome}": ${semVaga}`;
  if (nomeEmUso(outros, arquivado.nome)) {
    return `Já existe um cenário ativo chamado "${arquivado.nome}". Renomeie o outro antes de restaurar este.`;
  }
  return null;
}

/**
 * Para onde ir depois de arquivar o cenário ABERTO: o ativo seguinte na ordem do seletor;
 * se era o último, o anterior; se era o único, nenhum (`null` = o estado vazio).
 */
export function proximoAposArquivar(ativos: ReadonlyArray<{ id: string }>, arquivadoId: string): string | null {
  const i = ativos.findIndex((c) => c.id === arquivadoId);
  const resto = ativos.filter((c) => c.id !== arquivadoId);
  if (resto.length === 0) return null;
  if (i < 0) return resto[0]!.id;
  return (resto[i] ?? resto[i - 1] ?? resto[0])!.id;
}

/** O nome sugerido para a cópia — e, se já existir, "(cópia 2)", "(cópia 3)"… */
export function nomeDaCopia(ativos: ReadonlyArray<{ id: string; nome: string }>, original: string): string {
  const base = `${original} (cópia)`;
  if (!nomeEmUso(ativos, base)) return base;
  for (let n = 2; n < 100; n++) {
    const tentativa = `${original} (cópia ${n})`;
    if (!nomeEmUso(ativos, tentativa)) return tentativa;
  }
  return base;
}
